import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarPlus, CalendarClock } from "lucide-react";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { formatMoney } from "@/lib/currency";
import { useTenantMe } from "@/lib/tenant-me";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/v2/StatCard";
import { StatusBadge } from "@/components/v2/StatusBadge";
import { PageHeader } from "@/components/v2/PageHeader";
import { PlanUsageBanner } from "@/components/v2/PlanUsageBanner";
import { DataTable, type Column } from "@/components/v2/DataTable";
import { EmptyState } from "@/components/v2/EmptyState";
import { formatPreferredDate, parseDateLocal, formatTimeMaybe } from "@/lib/booking-date";

/**
 * Dashboard — Pencil frame `p2Gh0`.
 *
 * Layout:
 *   - Greeting "Good morning, <Name>" + today's date subtitle
 *   - 4 KPI StatCards (Bookings today / Revenue today / Pending review / Low-stock)
 *   - Two-column body: left = Recent Bookings (5-col table) · right = Upcoming Schedule rail
 *
 * Recent Bookings uses `/api/v1/bookings?size=5` (not the dashboard `recentBookings`)
 * so we can show client + service columns matching the Pencil reference.
 */

type BookingRow = {
  id?: string;
  customerName?: string;
  serviceName?: string | null;
  preferredDate?: string | null;
  status?: string;
  price?: number | string;
  currency?: string;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const todayLabel = () =>
  new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export function Home() {
  const email = useAuthStore((s) => s.email);
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useTenantMe();
  const tenantCurrency = tenant.data?.tenant.currency ?? "PHP";
  const firstName = (email ?? "").split("@")[0].split(/[._-]/)[0];
  const displayName = firstName ? firstName[0].toUpperCase() + firstName.slice(1) : "there";

  const overview = useQuery({
    queryKey: ["dashboard.overview"],
    enabled: !!accessToken,
    queryFn: async () => unwrap(await api.GET("/api/v1/dashboard/overview", {})),
  });

  // Use the bookings list for the Recent Bookings table — DashboardBookingRow
  // doesn't include client / service so we'd lose the Pencil layout columns.
  const recent = useQuery({
    queryKey: ["bookings.recent"],
    enabled: !!accessToken,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/bookings", {
          params: { query: { page: 0, size: 5 } },
        }),
      ),
  });

  const recentColumns: Column<BookingRow>[] = [
    {
      key: "client",
      header: "Client",
      cell: (r) => (
        <span className="font-medium text-sm">{r.customerName || "(walk-in)"}</span>
      ),
    },
    {
      key: "service",
      header: "Service",
      cell: (r) => (
        <span className="text-sm text-muted-foreground">{r.serviceName ?? "—"}</span>
      ),
    },
    {
      key: "date",
      header: "Date",
      cell: (r) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {r.preferredDate ? formatPreferredDate(r.preferredDate) : "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => <StatusBadge label={r.status ?? "—"} />,
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      cell: (r) => (
        <span className="font-semibold text-sm">{formatMoney(r.currency, r.price)}</span>
      ),
    },
  ];

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title={`${greeting()}, ${displayName}`}
        subtitle={todayLabel()}
        actions={
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to="/bookings">View bookings</Link>
            </Button>
            <Button size="sm" asChild>
              <Link to="/services">+ New service</Link>
            </Button>
          </>
        }
      />

      <PlanUsageBanner />

      {overview.isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-xl border bg-card p-5 h-28 animate-pulse" />
          ))}
        </div>
      )}

      {overview.isError && (
        <div className="rounded-xl border border-destructive/40 bg-status-bg-cancelled px-4 py-3 text-sm text-status-cancelled">
          Couldn't load dashboard: {(overview.error as Error).message}
        </div>
      )}

      {overview.data && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard
              label="Bookings today"
              value={overview.data.bookingsToday ?? 0}
              hint={`${overview.data.confirmedToday ?? 0} confirmed`}
            />
            <StatCard
              label="Revenue today"
              value={formatMoney(
                tenantCurrency,
                overview.data.revenueToday as unknown as number | string | undefined,
              )}
              hint={
                overview.data.canceledToday
                  ? `${overview.data.canceledToday} canceled today`
                  : "No cancellations"
              }
            />
            <StatCard
              label="Pending review"
              value={Number(overview.data.verifyingQueueSize ?? 0)}
              hint={
                overview.data.verifyingQueueSize ? "Receipts awaiting review" : "All caught up"
              }
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold tracking-tight">Recent bookings</h2>
                <Link
                  to="/bookings"
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  View all →
                </Link>
              </div>
              <DataTable
                columns={recentColumns}
                rows={(recent.data?.content as BookingRow[] | undefined) ?? []}
                rowKey={(r) => r.id ?? Math.random().toString()}
                onRowClick={(r) => {
                  if (r.id) window.location.href = `/bookings?focus=${r.id}`;
                }}
                emptyState={
                  <EmptyState
                    title="No bookings yet"
                    description="When clients schedule appointments, they'll appear here."
                    action={
                      <Button size="sm" asChild>
                        <Link to="/services">+ New booking</Link>
                      </Button>
                    }
                  />
                }
              />
            </section>

            <aside className="space-y-3">
              <h2 className="text-base font-semibold tracking-tight">Upcoming schedule</h2>
              <UpcomingSchedule />
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

function UpcomingSchedule() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const { data, isLoading } = useQuery({
    queryKey: ["bookings.upcoming"],
    enabled: !!accessToken,
    queryFn: async () => {
      const start = new Date().toISOString();
      const end = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      return unwrap(
        await api.GET("/api/v1/bookings", {
          params: { query: { start, end, size: 6 } },
        }),
      );
    },
  });

  const items = data?.content ?? [];

  if (isLoading) {
    return <div className="rounded-xl border bg-card h-48 animate-pulse" />;
  }

  if (items.length === 0) {
    return (
      <EmptyState
        icon={CalendarPlus}
        title="Nothing on the calendar"
        description="Upcoming appointments for the next two weeks will show here."
      />
    );
  }

  return (
    <ol className="rounded-xl border bg-card divide-y">
      {items.map((b) => {
        const pd = (b as { preferredDate?: string | null }).preferredDate;
        const dt = pd ? parseDateLocal(pd) : null;
        const isToday = dt && dt.toDateString() === new Date().toDateString();
        const isTomorrow =
          dt &&
          dt.toDateString() ===
            new Date(Date.now() + 24 * 60 * 60 * 1000).toDateString();
        const dayLabel = isToday
          ? "Today"
          : isTomorrow
            ? "Tomorrow"
            : dt?.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        const timeLabel = pd ? formatTimeMaybe(pd) : "";
        return (
          <li key={b.id} className="text-sm">
            <Link
              to={`/bookings?focus=${b.id}`}
              className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30"
            >
              <CalendarClock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">
                  {b.customerName || "(walk-in)"}{" "}
                  <span className="text-muted-foreground font-normal">
                    · {b.serviceName ?? "Booking"}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {dayLabel ?? "—"}
                  {timeLabel ? `, ${timeLabel}` : ""}
                </p>
              </div>
            </Link>
          </li>
        );
      })}
    </ol>
  );
}
